import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import '../matches/MatchMarket.css';

/**
 * TournamentMarket — parimutuella futures på turneringsutfall (A2).
 *
 * Två marknader: vinnare (champion) och finalist. Varje marknad är sin
 * egen pott; vinnande insatser delar hela potten proportionellt (huset
 * tar inget). Insatser tas emot tills bracketen genereras — därefter
 * visas facit per insats. Deltagare kan bara satsa på sin egen anmälan
 * (API:t upprätthåller regeln).
 *
 * Renderar null när marknaden är avstängd (SiteConfig
 * TOURNAMENT_MARKET_ENABLED) eller när den är stängd och man saknar
 * egna insatser.
 *
 * @param {{tournamentId: string}} props Turneringens id.
 * @returns {JSX.Element|null} Marknadssektionen.
 */
export default function TournamentMarket({ tournamentId }) {
  const { t } = useTranslation();
  const { user, getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [market, setMarket] = useState('champion');
  const [pick, setPick] = useState(null);
  const [amount, setAmount] = useState('');
  const [placing, setPlacing] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(() => {
    fetch(`${API_URL}/api/predictions/market/tournament/${tournamentId}`, {
      headers: getAuthHeaders(),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => { if (body) setData(body); })
      .catch(() => {
        // tyst — marknaden är icke-kritisk för sidan
      });
  }, [tournamentId, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  if (!data || !data.enabled) return null;
  const myStakes = data.my_stakes || [];
  if (!data.open && myStakes.length === 0) return null;

  const locale = currentLocale();
  const pools = data.markets[market] || { total: 0, picks: {} };
  const pickName = (regId) => {
    const p = data.picks.find((x) => x.registration_id === regId);
    return p ? p.display_name : '?';
  };

  /**
   * Lägg insatsen på vald anmälan i vald marknad.
   */
  const placeStake = async () => {
    if (!pick || !amount) return;
    setPlacing(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/predictions/market/tournament/stake`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tournament_id: tournamentId,
          market,
          registration_id: pick,
          amount: Number(amount),
        }),
      });
      const body = await res.json();
      if (!res.ok) {
        const key = {
          insufficient_balance: 'market.errInsufficient',
          stake_out_of_bounds: 'market.errBounds',
          own_team_only: 'tournMarket.errOwnTeam',
          market_locked: 'tournMarket.lockedHint',
        }[body.code];
        setMessage({ kind: 'error', text: key ? t(key) : (body.error || t('market.errGeneric')) });
      } else {
        setMessage({ kind: 'success', text: t('market.stakePlaced') });
        setAmount('');
        setPick(null);
        window.dispatchEvent(new CustomEvent('wallet-refresh'));
        load();
      }
    } catch {
      setMessage({ kind: 'error', text: t('market.errGeneric') });
    } finally {
      setPlacing(false);
    }
  };

  /**
   * Facit-etikett för en egen insats efter settlement.
   *
   * @param {object} stake Insatsrad från API:t.
   * @returns {JSX.Element} Status-span med färgklass.
   */
  const stakeResult = (stake) => {
    if (stake.payout === null) {
      return <span className="market-stake-result pending">⏳</span>;
    }
    if (stake.is_void) {
      return <span className="market-stake-result void">↩️ +{stake.payout}</span>;
    }
    if (stake.payout > 0) {
      return <span className="market-stake-result won">🏆 +{stake.payout}</span>;
    }
    return <span className="market-stake-result lost">−{stake.amount}</span>;
  };

  return (
    <div className="tournament-market">
      <div className="market-header">
        <span className="market-title">📈 {t('tournMarket.title')}</span>
        <span className="market-pot">
          {t('market.pot')}: 🪙 {pools.total.toLocaleString(locale)}
        </span>
      </div>

      <div className="tournament-market-tabs">
        <button
          type="button"
          className={`tournament-market-tab ${market === 'champion' ? 'active' : ''}`}
          onClick={() => { setMarket('champion'); setPick(null); }}
        >
          🏆 {t('tournMarket.champion')}
        </button>
        <button
          type="button"
          className={`tournament-market-tab ${market === 'finalist' ? 'active' : ''}`}
          onClick={() => { setMarket('finalist'); setPick(null); }}
        >
          🎯 {t('tournMarket.finalist')}
        </button>
      </div>

      {message && <div className={`market-message ${message.kind}`}>{message.text}</div>}

      {data.open && user && (
        <>
          <div className="tournament-market-picks">
            {data.picks.map((p) => {
              const poolData = pools.picks[p.registration_id];
              const staked = poolData ? poolData.amount : 0;
              const mult = poolData ? poolData.multiplier : null;
              return (
                <button
                  key={p.registration_id}
                  type="button"
                  className={`market-side-btn ${pick === p.registration_id ? 'selected' : ''}`}
                  onClick={() => setPick(pick === p.registration_id ? null : p.registration_id)}
                >
                  <span className="market-side-name">{p.display_name}</span>
                  <span className="market-side-pool">
                    🪙 {staked.toLocaleString(locale)}
                    {mult !== null && <span className="market-side-mult"> ×{mult}</span>}
                  </span>
                </button>
              );
            })}
          </div>
          {pick && (
            <div className="market-stake-form">
              <input
                className="market-amount-input"
                type="number"
                min={data.min_stake}
                max={data.max_stake > 0 ? data.max_stake : undefined}
                placeholder={t('market.amountPlaceholder', { min: data.min_stake })}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <button
                type="button"
                className="market-stake-btn"
                disabled={placing || !amount || Number(amount) < data.min_stake}
                onClick={placeStake}
              >
                {placing ? t('market.placing') : t('market.placeStake')}
              </button>
            </div>
          )}
          <p className="market-hint">
            {t('tournMarket.hint', { min: data.min_stake })}
            {data.max_stake > 0 && ` ${t('market.hintMax', { max: data.max_stake })}`}
          </p>
        </>
      )}

      {data.open && !user && (
        <p className="market-hint">{t('market.loginToStake')}</p>
      )}

      {!data.open && !data.finished && (
        <p className="market-hint">{t('tournMarket.lockedHint')}</p>
      )}

      {myStakes.length > 0 && (
        <div className="market-my-stakes">
          {myStakes.map((stake) => (
            <div key={stake.id} className="market-my-stake-row">
              <span className="market-my-stake-pick">
                {stake.market === 'champion' ? '🏆' : '🎯'} {pickName(stake.pick_id)}
              </span>
              <span className="market-my-stake-amount">🪙 {stake.amount}</span>
              {stakeResult(stake)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
